/**
 * @copyright (C) 2024-2025, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Jinhyoung Jang (jangjh@rsupport.com)
 *
 * @description
 *
 * @created at 2025-02-06 17:25:30
 **/

import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from "axios";
import IWebServiceOptions from "./WebServiceOptions";

export const TIMEOUT = 4 * 1000 * 60;
export const DEFAULT_REQUEST_HEADERS = {
	Accept: "application/json",
	"Content-Type": "application/json; charset=UTF-8"
};

export default class WebService {
	#instance: AxiosInstance | null = null;

	protected constructor(protected readonly _options: IWebServiceOptions) {
		this.initialize().then(() => {});
	}

	// property
	protected get instance() {
		if (this.#instance === null) {
			throw new Error("BaseService instance is not initialized");
		}
		return this.#instance;
	}

	protected async initialize() {
		this.#instance = axios.create({
			baseURL: this._options.base_url,
			headers: {
				...DEFAULT_REQUEST_HEADERS,
				...(this._options.headers ?? {})
			},
			timeout: this._options.timeout ?? TIMEOUT
		});

		this.instance.interceptors.request.use(this.on_before_request.bind(this), this.on_request_error.bind(this));
		this.instance.interceptors.response.use(this.on_after_response.bind(this), this.on_response_error.bind(this));
	}

	protected async on_before_request(config: InternalAxiosRequestConfig) {
		config.baseURL = this._options.base_url;
		if (this._options.authorization_key !== undefined && config.headers) {
			config.headers["Authorization"] = this._options.authorization_key;
		}
		if (this._options.with_credentials !== undefined) {
			config.withCredentials = this._options.with_credentials;
		}
		if (this._options.timeout !== undefined) {
			config.timeout = this._options.timeout;
		}
		return this._options.on_before_requst?.(config) ?? config;
	}

	protected on_request_error(error: AxiosError) {
		return Promise.reject(this._options.on_request_error?.(error) ?? error);
	}

	protected on_after_response(response: AxiosResponse) {
		return this._options.on_after_response?.(response) ?? response;
	}

	protected on_response_error(error: AxiosError) {
		const new_error = this._options.on_response_error?.(error);
		if (new_error === null) {
			return;
		}
		return Promise.reject(new_error ?? error);
	}

	protected fetch<T = unknown>(url: string, config: object): Promise<AxiosResponse<T>> {
		return this.instance
			.request<T, AxiosResponse<T>>({
				url,
				...config
			})
			.catch((error: unknown) => {
				throw error;
			});
	}

	protected get<T = unknown>(url: string, params = {}, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
		return this.instance
			.get<T, AxiosResponse<T>>(url, {
				params,
				...config
			})
			.catch((error: unknown) => {
				throw error;
			});
	}

	protected post<T = unknown>(url: string, payload = {}, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
		return this.instance.post<T, AxiosResponse<T>>(url, payload, config).catch((error: unknown) => {
			throw error;
		});
	}

	protected put<T = unknown>(url: string, payload = {}, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
		return this.instance.put<T, AxiosResponse<T>>(url, payload, config).catch((error: unknown) => {
			throw error;
		});
	}

	protected patch<T = unknown>(url: string, payload = {}, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
		return this.instance.patch<T, AxiosResponse<T>>(url, payload, config).catch((error: unknown) => {
			throw error;
		});
	}

	protected delete<T = unknown>(url: string, payload = {}, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
		return this.instance
			.delete<T, AxiosResponse<T>>(url, {
				...(config ? config : {}),
				...(config && config.data ? {} : { data: payload })
			})
			.catch((error: unknown) => {
				throw error;
			});
	}

	protected upload<T = unknown>(
		url: string,
		payload = {},
		config: AxiosRequestConfig = {
			headers: { "Content-Type": "multipart/form-data" }
		}
	): Promise<AxiosResponse<T>> {
		return this.instance.post<T, AxiosResponse<T>>(url, payload, config).catch((error: unknown) => {
			throw error;
		});
	}
}
